export function getSubdomain() {
  const host = window.location.hostname;
  const baseVar = window.location.protocol + "//" + host + "/api/v2/";
  return baseVar;
}

export function getApiBase() {
  return window.location.protocol + '//' + window.location.hostname + '/api/v2/';
}

export function getBaseDomain() {
  const host = window.location.hostname;
  return host.substring(host.indexOf('.') + 1);
}

/**
 * #1849 — the ONE host Apple sign-in runs on, for this environment's TLD.
 *
 * The Apple SDK used to be initialised with `redirectURI = window.location.href`,
 * i.e. whatever host the user happened to be standing on. Apple validates that
 * against the Return URLs registered on the Services ID, so EVERY host that
 * showed the button had to be hand-registered in Apple's developer console —
 * which made a console edit at Apple a required step of onboarding a local.
 * Nobody remembers, so every new tenant subdomain shipped with a visibly broken
 * Apple button and no error on our side (Apple rejects it before the request
 * reaches us). Apple also caps the domains and return URLs on a Services ID, so
 * "just add them all" fails at exactly the moment you onboard one.
 *
 * Measured 2026-09-05 against `appleid.apple.com/auth/authorize` with the real
 * Services ID: `login`/`labour`/`admin.callsteward.com` are accepted; every
 * tenant subdomain AND `login.callsteward.ca` return
 * `{"errorMessage":"Invalid web redirect url."}`.
 *
 * ── WHY THIS RETURNS A HOST AND NOT JUST A STRING ────────────────────────────
 *
 * A fixed redirectURI alone is NOT sufficient, because the flow is a POPUP
 * (`usePopup: true`, which forces `response_mode=web_message`). The authorize
 * URL the SDK builds carries no opener-origin parameter, so `redirect_uri` is
 * the only thing identifying the caller's origin, and Apple posts the result
 * back to THAT origin. Pointing a tenant subdomain's popup at the login host
 * would pass validation and then deliver the answer to a window that is not
 * listening — a hang instead of an error page, which is worse.
 *
 * So the sign-in has to actually RUN on this host. Callers that are not on it
 * send the user here first; the existing post-login `#/switchClient` step then
 * takes them onward exactly as it does for anyone who starts at `login.`.
 *
 * ── ONE ENTRY PER TLD, AND THAT IS THE CEILING ───────────────────────────────
 *
 * `.com` and `.ca` are SEPARATE DATABASES, so a single global address cannot
 * serve both — a `.ca` member sent to `login.callsteward.com` would authenticate
 * against the wrong tenant estate. The redirect is therefore derived from the
 * current TLD, which gives exactly TWO addresses, forever, no matter how many
 * locals are onboarded:
 *
 *     https://login.callsteward.com/     registered at Apple ✅
 *     https://login.callsteward.ca/      NOT registered ❌ — see #1849
 *
 * ⚠ `.ca` Apple sign-in stays broken until that ONE return URL is added in the
 * Apple developer console. That is a one-time action, not a per-local one, and
 * it is the whole point of this change.
 *
 * Returns null when the host has no TLD to derive from (localhost, an IP, a
 * preview box) so callers fall back rather than navigate somewhere that 404s.
 */
export function getAppleSignInHost(hostname) {
  const host = (hostname || window.location.hostname || '').toLowerCase();

  // No dot means no base domain to build on — localhost and bare hostnames.
  if (host.indexOf('.') === -1) return null;

  // An IP address has dots but no login host; `login.10.0.0.5` is not a place.
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;

  return 'login.' + host.substring(host.indexOf('.') + 1);
}

/**
 * #1849 — the fixed `redirectURI` handed to the Apple SDK. Always the login host
 * for this TLD, never `window.location`. Null on a host with no login address,
 * where the caller must not offer Apple sign-in at all.
 */
export function getAppleRedirectURI(hostname) {
  const appleHost = getAppleSignInHost(hostname);

  return appleHost ? 'https://' + appleHost + '/' : null;
}

/** #1849 — true when Apple sign-in can run in place, i.e. we ARE the login host. */
export function appleSignInRunsHere(hostname) {
  const host = (hostname || window.location.hostname || '').toLowerCase();

  return !!getAppleSignInHost(host) && getAppleSignInHost(host) === host;
}

export function getImageBase() {
  return window.location.protocol + '//' + window.location.hostname + '/';
}

/**
 * #1694 — the host whose Android App Link can foreground the native app.
 *
 * App Link handoff only happens on a host the device has VERIFIED for
 * com.callsteward.mobile. Exactly three are verified (`pm get-app-links`):
 *
 *     stage-callsteward.com · admin.callsteward.com · admin.callsteward.ca
 *
 * The desktop web app does NOT run on any of them — it runs on `login.` and on
 * tenant subdomains — so `window.location.origin` is never the right target:
 * `https://login.stage-callsteward.com/open/mobile` resolves to Chrome and stays
 * in the browser. App Links do not require same-origin, so we deliberately send
 * the user cross-origin to the verified host for THIS environment.
 *
 * Matching is on the environment's own domain, so a stage session can only ever
 * resolve to the stage host — a stage page must never bounce to production (the
 * old `cs3.me/confirm2` path did exactly that: stage → login.callsteward.com).
 *
 * Returns null when the host is unrecognised (a preview box, an IP, localhost),
 * so callers can fall back to in-browser behaviour rather than navigate to a
 * host that would 404 or cross environments.
 */
export function getAppLinkHost(hostname) {
  const host = (hostname || window.location.hostname || '').toLowerCase();

  // Ordered: the stage domain is checked first so it can never fall through to
  // a production match.
  const VERIFIED = [
    { domain: 'stage-callsteward.com', appLinkHost: 'stage-callsteward.com' },
    { domain: 'callsteward.ca', appLinkHost: 'admin.callsteward.ca' },
    { domain: 'callsteward.com', appLinkHost: 'admin.callsteward.com' },
  ];

  for (let i = 0; i < VERIFIED.length; i++) {
    const entry = VERIFIED[i];
    if (host === entry.domain || host.endsWith('.' + entry.domain)) {
      return entry.appLinkHost;
    }
  }

  return null;
}

/** #1694 — the full deep link that foregrounds the app, or null if unavailable. */
export function getReturnToAppUrl(hostname) {
  const appLinkHost = getAppLinkHost(hostname);
  return appLinkHost ? 'https://' + appLinkHost + '/open/mobile' : null;
}

// #1478 — apply headers to a raw XMLHttpRequest, skipping any value that is
// null/undefined or the literal strings "undefined"/"null". Mirrors the axios
// api.js request-interceptor sanitizer for the Drive preview/download/upload
// paths that use raw XHR (which bypass the axios interceptor entirely). Without
// this, setRequestHeader('cs3-context-role', undefined) coerces to the literal
// "undefined" and trips ValidateRoleHeader once #1432 flips to enforce.
export function safeSetXhrHeaders(xhr, headers) {
  Object.keys(headers || {}).forEach(function (key) {
    var v = headers[key];
    if (v === undefined || v === null || v === 'undefined' || v === 'null') return;
    xhr.setRequestHeader(key, v);
  });
}

export const global = [
    {
        'BASE_URL': getSubdomain(),
        'IMAGE_URL': getImageBase(),
        'CLIENT_BASE': getBaseDomain() + '/',
        'REPORT_URL': "https://reports.callsteward.com/"
    },
];
